import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useStore } from "../../../stores/helpers/useStore";
import { observer } from "mobx-react-lite";
import Footer from "../../../components/Footer/Footer";
import "./LoginPage.css";

const initialValues = {
	login: "",
	password: "",
};

const loginationSchema = Yup.object().shape({
	login: Yup.string().trim().required("Email or room code is required"),
	password: Yup.string().trim().required("Password is required"),
});

const LoginPage = (props) => {
	const { authStore } = useStore();
	const { history } = props;
	const [studioSetup, setStudioSetup] = useState(null);
	const [continueEmail, setContinueEmail] = useState("");
	const [continueError, setContinueError] = useState("");
	const [isContinuing, setIsContinuing] = useState(false);

	const formik = useFormik({
		initialValues,
		validationSchema: loginationSchema,
		onSubmit: async (values, { setFieldError, setSubmitting }) => {
			setSubmitting(true);
			setStudioSetup(null);
			setContinueError("");
			try {
				const response = await authStore.login(values.login, values.password);
				if (response?.status === "needsStudioSetup") {
					setStudioSetup(response);
					setContinueEmail(response.email || "");
					setSubmitting(false);
					return;
				}
			} catch (err) {
				setSubmitting(false);
				const message = err?.response?.data?.message;
				if (err?.response?.status === 409) {
					setFieldError(
						err?.response?.data?.param === "password" ? "password" : "login",
						message || "Invalid input."
					);
				} else if (err?.response?.status === 406) {
					setFieldError("login", message || "No account found for that login.");
				} else if (err?.response?.status === 403) {
					setFieldError("password", message || "Wrong credentials.");
				} else {
					setFieldError("login", message || "Sign in failed. Please try again.");
				}
			}
		},
	});

	const handleContinue = async () => {
		if (!studioSetup?.setupToken) {
			return;
		}

		const needsEmail = !studioSetup.email;
		const emailToSend = needsEmail
			? continueEmail.trim()
			: studioSetup.email;

		if (needsEmail) {
			if (!emailToSend) {
				setContinueError("Email is required to create your Studio account.");
				return;
			}
			if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToSend)) {
				setContinueError("Please enter a valid email.");
				return;
			}
		}

		setIsContinuing(true);
		setContinueError("");
		try {
			await authStore.continueFromHost(
				studioSetup.setupToken,
				needsEmail ? emailToSend : undefined
			);
			history.push("/hosts");
		} catch (err) {
			setIsContinuing(false);
			const message =
				err?.response?.data?.message ||
				"Could not create Studio account. Please try again.";
			setContinueError(message);
		}
	};

	const handleBackToLogin = () => {
		setStudioSetup(null);
		setContinueEmail("");
		setContinueError("");
	};

	return (
		<>
			<div className="login-page">
				<div className="login-container">
					<div className="login-header">
						<div className="login-badge">ENTERPRISE</div>
						{studioSetup ? (
							<>
								<h1>Host Account Found</h1>
								<p>Continue to set up your Studio account</p>
							</>
						) : (
							<>
								<h1>Welcome Back</h1>
								<p>Sign in to TriviaRat Enterprise</p>
							</>
						)}
					</div>

					{studioSetup ? (
						<div className="login-form studio-setup">
							<p className="studio-setup-message">
								We found your TriviaRat host account
								{studioSetup.roomCode ? (
									<>
										{" "}
										for room <b>{studioSetup.roomCode}</b>
									</>
								) : null}
								. Continue to create your Studio account and link this host.
							</p>

							{studioSetup.email ? (
								<div className="studio-setup-detail">
									<span className="studio-setup-label">Studio email</span>
									<span>{studioSetup.email}</span>
								</div>
							) : (
								<div className="form-group">
									<label>Studio Email</label>
									<input
										type="email"
										name="continueEmail"
										placeholder="you@company.com"
										value={continueEmail}
										onChange={(e) => setContinueEmail(e.target.value)}
										className={continueError ? "error" : ""}
									/>
									<p className="studio-setup-hint">
										Your host account has no email yet. This will be used for
										Studio login going forward.
									</p>
								</div>
							)}

							{continueError && (
								<span className="error-text">{continueError}</span>
							)}

							<button
								className="submit-button"
								type="button"
								onClick={handleContinue}
								disabled={isContinuing}
							>
								{isContinuing ? (
									<>
										<span className="spinner"></span>
										Setting up...
									</>
								) : (
									"Continue"
								)}
							</button>

							<button
								type="button"
								className="link-button studio-setup-back"
								onClick={handleBackToLogin}
								disabled={isContinuing}
							>
								Back to sign in
							</button>
						</div>
					) : (
						<form
							onSubmit={formik.handleSubmit}
							noValidate
							className="login-form"
						>
							<div className="form-group">
								<label>Email or Room Code</label>
								<input
									type="text"
									name="login"
									placeholder="you@company.com or ROOMCODE"
									autoComplete="username"
									value={formik.values.login}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									className={
										formik.touched.login && formik.errors.login ? "error" : ""
									}
								/>
								{formik.touched.login && formik.errors.login && (
									<span className="error-text">{formik.errors.login}</span>
								)}
							</div>

							<div className="form-group">
								<label>Password</label>
								<input
									type="password"
									name="password"
									placeholder="Enter your password"
									autoComplete="current-password"
									value={formik.values.password}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									className={
										formik.touched.password && formik.errors.password
											? "error"
											: ""
									}
								/>
								{formik.touched.password && formik.errors.password && (
									<span className="error-text">{formik.errors.password}</span>
								)}
							</div>

							<button
								type="button"
								className="forgot-password-link"
								onClick={() => history.push("/pages/auth/forgot-password")}
							>
								Forgot password?
							</button>

							<button
								className="submit-button"
								type="submit"
								disabled={formik.isSubmitting}
							>
								{formik.isSubmitting ? (
									<>
										<span className="spinner"></span>
										Signing in...
									</>
								) : (
									"Sign In"
								)}
							</button>

							<div className="form-footer">
								<p>Don't have an account?</p>
								<button
									type="button"
									className="link-button"
									onClick={() => history.push("/pages/auth/register")}
								>
									Create Enterprise Account
								</button>
							</div>
						</form>
					)}
				</div>
			</div>
			<Footer />
		</>
	);
};

export default observer(LoginPage);
